import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LOCATIONS, ROLES } from "../constants";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const initial = {
  firstName: "", lastName: "", email: "", password: "", confirm: "",
  birthdate: "", startDate: "", primaryLocation: "", role: "",
};

export default function Register() {
  const { doRegister } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(initial);
  const [errors, setErrors] = useState({});

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!f.firstName.trim()) e.firstName = "First name is required.";
    if (!f.lastName.trim()) e.lastName = "Last name is required.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "Enter a valid email.";
    if (f.password.length < 6) e.password = "Password must be at least 6 characters.";
    if (f.confirm !== f.password) e.confirm = "Passwords do not match.";
    if (!f.birthdate) e.birthdate = "Birthdate is required.";
    if (!f.startDate) e.startDate = "Start date is required.";
    if (!f.primaryLocation) e.primaryLocation = "Primary location is required.";
    if (!f.role) e.role = "Role is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const [busy, setBusy] = useState(false);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const res = await doRegister(f);
      if (res.error) { setErrors({ email: res.error }); return; }
      if (res.pending) {
        setF(initial);
        toast.success(res.message || "Account created — it's now awaiting HR/Director approval. You'll be able to sign in once approved.", { duration: 6000 });
        navigate("/login");
        return;
      }
      toast.success("Account created! Welcome to The Vault.");
      navigate("/app/dashboard");
    } finally {
      setBusy(false);
    }
  };

  const fieldCls = "bg-vault-panel2 border-white/10 text-vault-cream placeholder:text-vault-subtle/60 h-11";
  const Err = ({ k }) => errors[k] ? <p className="text-xs text-red-400 mt-1">{errors[k]}</p> : null;

  return (
    <div className="min-h-screen bg-vault-bg">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-vault-panel/70 hover:text-vault-panel transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
      </div>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
        <div className="rounded-3xl bg-vault-panel p-8 sm:p-10 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.6)]">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-accent mb-2">Join Clark Academy</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-vault-cream">Create your account</h1>
          <p className="text-sm text-vault-subtle mt-2">Set up your member profile to start earning gems.</p>

          <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-vault-subtle">First Name</Label>
              <Input value={f.firstName} onChange={(e) => set("firstName", e.target.value)} className={fieldCls} placeholder="Emma" />
              <Err k="firstName" />
            </div>
            <div className="space-y-2">
              <Label className="text-vault-subtle">Last Name</Label>
              <Input value={f.lastName} onChange={(e) => set("lastName", e.target.value)} className={fieldCls} placeholder="Stone" />
              <Err k="lastName" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-vault-subtle">Email</Label>
              <Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={fieldCls} placeholder="you@clarkacademy.com" />
              <Err k="email" />
            </div>
            <div className="space-y-2">
              <Label className="text-vault-subtle">Password</Label>
              <Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className={fieldCls} placeholder="••••••••" />
              <Err k="password" />
            </div>
            <div className="space-y-2">
              <Label className="text-vault-subtle">Confirm Password</Label>
              <Input type="password" value={f.confirm} onChange={(e) => set("confirm", e.target.value)} className={fieldCls} placeholder="••••••••" />
              <Err k="confirm" />
            </div>
            <div className="space-y-2">
              <Label className="text-vault-subtle">Birthdate</Label>
              <Input type="date" value={f.birthdate} onChange={(e) => set("birthdate", e.target.value)} className={`${fieldCls} [color-scheme:dark]`} />
              <Err k="birthdate" />
            </div>
            <div className="space-y-2">
              <Label className="text-vault-subtle">Start Date</Label>
              <Input type="date" value={f.startDate} onChange={(e) => set("startDate", e.target.value)} className={`${fieldCls} [color-scheme:dark]`} />
              <Err k="startDate" />
            </div>
            <div className="space-y-2">
              <Label className="text-vault-subtle">Primary Location</Label>
              <Select value={f.primaryLocation} onValueChange={(v) => set("primaryLocation", v)}>
                <SelectTrigger className={fieldCls}><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Err k="primaryLocation" />
            </div>
            <div className="space-y-2">
              <Label className="text-vault-subtle">Select Role</Label>
              <Select value={f.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className={fieldCls}><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Err k="role" />
            </div>
            <div className="sm:col-span-2 mt-2">
              <button type="submit" disabled={busy} className="w-full rounded-2xl bg-vault-accent text-vault-panel font-bold py-3.5 hover:bg-vault-accentdark transition-colors disabled:opacity-60" data-testid="register-submit-button">
                {busy ? "Creating…" : "Create account"}
              </button>
              <p className="text-xs text-vault-subtle/70 mt-3 text-center">New accounts require HR or Director approval before you can sign in.</p>
            </div>
          </form>
          <p className="text-sm text-vault-subtle mt-6 text-center">
            Already a member? <Link to="/login" className="text-vault-accent font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
